import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../../assets/util/showToast";
import {
  wooGetOrders,
  wooUpdateOrder,
  WooOrder,
  WooOrderStatus,
} from "../../services/woo";

type SortKey = "id" | "date" | "customer" | "status" | "items" | "total";
type SortDir = "asc" | "desc";
type OrderStatus = "paid" | "pending" | "refunded" | "cancelled";

// Internal row type used by this screen
type OrderRow = {
  id: string; // display ID (Woo order number)
  internalId: number; // Woo internal numeric ID
  date: string; // ISO date string
  customer: string;
  status: OrderStatus;
  items: { qty: number }[];
  total: number;
  raw: WooOrder;
};

function formatUSD(v: number) {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD" });
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US");
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const map: Record<OrderStatus, string> = {
    paid: "success",
    pending: "warning",
    refunded: "danger",
    cancelled: "secondary",
  };
  return (
    <span className={`badge bg-${map[status]} text-white text-capitalize`}>
      {status}
    </span>
  );
}

function mapWooStatus(status: WooOrderStatus): OrderStatus {
  switch (status) {
    case "completed":
    case "processing":
      return "paid";
    case "refunded":
      return "refunded";
    case "cancelled":
    case "failed":
    case "trash":
      return "cancelled";
    case "pending":
    case "on-hold":
    default:
      return "pending";
  }
}

function mapWooOrderToRow(o: WooOrder): OrderRow {
  const fullName = `${o.billing?.first_name || ""} ${
    o.billing?.last_name || ""
  }`.trim();
  const customer = fullName || o.billing?.email || "Guest";

  return {
    id: o.number || String(o.id),
    internalId: o.id,
    date: o.date_created,
    customer,
    status: mapWooStatus(o.status),
    items: (o.line_items || []).map((li) => ({ qty: li.quantity })),
    total: parseFloat(o.total || "0"),
    raw: o,
  };
}

function isGuestOrder(order: OrderRow) {
  const billingName = `${order.raw?.billing?.first_name || ""} ${
    order.raw?.billing?.last_name || ""
  }`
    .trim()
    .toLowerCase();

  return (
    billingName === "guest" ||
    billingName === "Guest" ||
    billingName === "customer guest" ||
    order.customer.toLowerCase() === "guest"
  );
}

export default function Orders() {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const navigate = useNavigate();

  // ===== Filters =====
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | "active" | OrderStatus>(
    "active",
  );
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [minTotal, setMinTotal] = useState<string>("");
  const [maxTotal, setMaxTotal] = useState<string>("");

  // Load orders from WooCommerce
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        const { orders: wooOrders } = await wooGetOrders(1, 100);
        if (!alive) return;
        const mapped = wooOrders.map(mapWooOrderToRow);
        setOrders(mapped);
        setLoadError(null);
      } catch (e: any) {
        if (!alive) return;
        setLoadError(e?.message || "Failed to load orders");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ===== Sorting / pagination =====
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Derived list with filters
  const filtered = useMemo(() => {
    return orders
      .map((o) => ({
        ...o,
        itemsCount: o.items.reduce((sum, it) => sum + it.qty, 0),
      }))
      .filter((o) => {
        if (isGuestOrder(o)) return false;
        if (q) {
          const s = q.toLowerCase();
          if (
            !(
              o.id.toLowerCase().includes(s) ||
              o.customer.toLowerCase().includes(s)
            )
          ) {
            return false;
          }
        }
        if (status === "active") {
          if (o.status !== "paid" && o.status !== "pending") return false;
        } else if (status !== "all" && o.status !== status) return false;

        if (dateFrom && new Date(o.date) < new Date(dateFrom)) return false;
        if (dateTo && new Date(o.date) > new Date(dateTo)) return false;

        const minT = minTotal ? parseFloat(minTotal) : null;
        const maxT = maxTotal ? parseFloat(maxTotal) : null;
        if (minT !== null && o.total < minT) return false;
        if (maxT !== null && o.total > maxT) return false;

        return true;
      });
  }, [orders, q, status, dateFrom, dateTo, minTotal, maxTotal]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a: any, b: any) => {
      let va: any = a[sortKey];
      let vb: any = b[sortKey];
      if (sortKey === "date") {
        va = new Date(a.date).getTime();
        vb = new Date(b.date).getTime();
      }
      if (typeof va === "string" && typeof vb === "string") {
        return va.localeCompare(vb) * dir;
      }
      return (va - vb) * dir;
    });
  }, [filtered, sortKey, sortDir]);

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "date" ? "desc" : "asc");
    }
  }

  function resetFilters() {
    setQ("");
    setStatus("active");
    setDateFrom("");
    setDateTo("");
    setMinTotal("");
    setMaxTotal("");
    setPage(1);
    setSortKey("date");
    setSortDir("desc");
  }

  function onView(order: OrderRow) {
    // Use internal numeric ID in route for future single-order API
    navigate(`/account/orders/${order.internalId}`);
  }

  async function onRefund(order: OrderRow) {
    if (!window.confirm(`Mark order ${order.id} as refunded?`)) return;
    try {
      await wooUpdateOrder(order.internalId, { status: "refunded" });
      setOrders((prev) =>
        prev.map((o) =>
          o.internalId === order.internalId
            ? {
                ...o,
                status: "refunded",
                raw: { ...o.raw, status: "refunded" as WooOrderStatus },
              }
            : o
        )
      );
      showToast?.(`Order ${order.id} marked as refunded`, "success");
    } catch (e: any) {
      showToast?.(e?.message || `Failed to refund order ${order.id}`, "error");
    }
  }

  async function onCancel(order: OrderRow) {
    if (!window.confirm(`Cancel order ${order.id}?`)) return;
    try {
      await wooUpdateOrder(order.internalId, { status: "cancelled" });
      setOrders((prev) =>
        prev.map((o) =>
          o.internalId === order.internalId
            ? {
                ...o,
                status: "cancelled",
                raw: { ...o.raw, status: "cancelled" as WooOrderStatus },
              }
            : o
        )
      );
      showToast?.(`Order ${order.id} cancelled`, "info");
    } catch (e: any) {
      showToast?.(e?.message || `Failed to cancel order ${order.id}`, "error");
    }
  }

  return (
    <div className="page-layout">
      <div className="page-header">
        <h2 className="title">Orders</h2>
        <div className="right">
          {/* <a href="/account/orders/create" className="btn btn-primary">
            <i className="fa-solid fa-plus me-2" />
            Add Order
          </a> */}
        </div>
      </div>

      <div className="section-card">
        {loadError && (
          <div className="alert alert-danger mb-3" role="alert">
            {loadError}
          </div>
        )}
        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label mb-1">Search</label>
                <input
                  className="form-control"
                  placeholder="Search #ID or customer…"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label mb-1">Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as any);
                    setPage(1);
                  }}
                >
                  <option value="active">Paid + Pending</option>
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="refunded">Refunded</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label mb-1">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="col-6 col-md-2">
                <label className="form-label mb-1">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <div className="col-6 col-md-1">
                <label className="form-label mb-1">Min $</label>
                <input
                  type="number"
                  className="form-control"
                  value={minTotal}
                  onChange={(e) => {
                    setMinTotal(e.target.value);
                    setPage(1);
                  }}
                  min={0}
                />
              </div>

              <div className="col-6 col-md-1">
                <label className="form-label mb-1">Max $</label>
                <input
                  type="number"
                  className="form-control"
                  value={maxTotal}
                  onChange={(e) => {
                    setMaxTotal(e.target.value);
                    setPage(1);
                  }}
                  min={0}
                />
              </div>

              <div className="col-12 col-md-1 d-grid">
                <button
                  className="btn btn-outline-secondary"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div className="text-muted small">
                Showing <strong>{pageItems.length}</strong> of{" "}
                <strong>{total}</strong> orders
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small">Rows:</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 80 }}
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <Th
                      label="#"
                      onClick={() => toggleSort("id")}
                      active={sortKey === "id"}
                      dir={sortDir}
                    />
                    <Th
                      label="Date"
                      onClick={() => toggleSort("date")}
                      active={sortKey === "date"}
                      dir={sortDir}
                    />
                    <Th
                      label="Customer"
                      onClick={() => toggleSort("customer")}
                      active={sortKey === "customer"}
                      dir={sortDir}
                    />
                    <Th
                      label="Status"
                      onClick={() => toggleSort("status")}
                      active={sortKey === "status"}
                      dir={sortDir}
                    />
                    <Th
                      label="Items"
                      onClick={() => toggleSort("items")}
                      active={sortKey === "items"}
                      dir={sortDir}
                      className="text-end"
                    />
                    <Th
                      label="Total"
                      onClick={() => toggleSort("total")}
                      active={sortKey === "total"}
                      dir={sortDir}
                      className="text-end"
                    />
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        Loading orders...
                      </td>
                    </tr>
                  )}
                  {!loading &&
                    pageItems.map((o) => (
                      <tr key={o.id}>
                        <td>{o.id}</td>
                        <td>{formatDate(o.date)}</td>
                        <td>{o.customer}</td>
                        <td>
                          <StatusBadge status={o.status} />
                        </td>
                        <td className="text-end">
                          {o.items.reduce((sum, it) => sum + it.qty, 0)}
                        </td>
                        <td className="text-end">{formatUSD(o.total)}</td>
                        <td className="text-end">
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => onView(o)}
                            >
                              View
                            </button>
                            {o.status === "paid" && (
                              <button
                                className="btn btn-sm btn-outline-warning"
                                onClick={() => onRefund(o)}
                              >
                                Refund
                              </button>
                            )}
                            {o.status !== "cancelled" &&
                              o.status !== "refunded" && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => onCancel(o)}
                                >
                                  Cancel
                                </button>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        No orders match your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                Page {page} / {totalPages}
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹ Prev
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next ›
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary row (optional) */}
        <div className="card mt-3">
          <div className="card-body d-flex flex-wrap gap-3">
            <div>
              <span className="text-muted small">
                Total Revenue (filtered):{" "}
              </span>
              <strong>
                {formatUSD(sorted.reduce((sum, o) => sum + o.total, 0))}
              </strong>
            </div>
            <div>
              <span className="text-muted small">Orders: </span>
              <strong>{sorted.length}</strong>
            </div>
            <div>
              <span className="text-muted small">Avg Order Value: </span>
              <strong>
                {formatUSD(
                  sorted.length
                    ? sorted.reduce((s, o) => s + o.total, 0) / sorted.length
                    : 0,
                )}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({
  label,
  onClick,
  active,
  dir,
  className = "",
}: {
  label: string;
  onClick: () => void;
  active: boolean;
  dir: SortDir;
  className?: string;
}) {
  return (
    <th
      role="button"
      onClick={onClick}
      className={`user-select-none ${className}`}
      title="Sort"
    >
      {label}{" "}
      {active ? (
        <i
          className={`ms-1 fa-solid fa-sort-${dir === "asc" ? "up" : "down"}`}
        />
      ) : (
        <i className="ms-1 fa-solid fa-sort text-muted" />
      )}
    </th>
  );
}
